import { Button, Container, Grid, Paper, Stack, Typography, Box } from '@mui/material';
import { FormProvider, SelectTag, TextField } from '../../../../../components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { BASE_URL, dropdownFetch, toast } from '../../../../../utils';
import { SERVICE_TYPE } from '../../../../../constants';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const SERVICE_TYPE_MENUS = [{ _id: 'all', name: 'All' }, ...SERVICE_TYPE];

export const ServiceCollectionReport = () => {
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [serviceDropdown, setServiceDropdown] = useState([]);

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        service: Yup.string(),
        service_type: Yup.string(),
        from_date: Yup.string().required(' '),
        to_date: Yup.string().required(' ')
      })
    ),
    defaultValues: {
      service: 'all',
      service_type: 'all',
      from_date: new Date().toISOString().split('T')[0],
      to_date: new Date().toISOString().split('T')[0]
    },
    mode: 'all'
  });

  const { watch, setValue } = methods;
  const selectedServiceType = watch('service_type');

  const getServiceDropdown = useCallback(async () => {
    const data = await dropdownFetch('service');
    setServiceDropdown(data);
  }, []);

  useEffect(() => {
    getServiceDropdown();
  }, [getServiceDropdown]);

  // Reset the specific Service selection whenever Service Type changes, so a
  // service from a different type can't remain selected behind the filter.
  useEffect(() => {
    setValue('service', 'all');
  }, [selectedServiceType, setValue]);

  const filteredServiceDropdown =
    selectedServiceType === 'all' ? serviceDropdown : serviceDropdown.filter((s) => s.type === selectedServiceType);

  function formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type).value;
    return `${get('day')}/${get('month')}/${get('year')}`;
  }

  const buildRefId = () =>
    JSON.stringify({
      service: watch('service'),
      service_type: watch('service_type'),
      from_date: watch('from_date'),
      to_date: watch('to_date')
    });

  const noFilterSelected = watch('service') === 'all' && watch('service_type') === 'all';

  const handleExcelReport = async () => {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    try {
      const response = await axios.get(
        `${BASE_URL}/print/hospital/outpatient-management/service-collection-report?refId=${buildRefId()}`
      );

      if (!response.data.success) {
        toast('Something Went Wrong', { variant: 'error' });
        return;
      }

      const d = response.data.data;
      const opdRows = d?.opd?.data || [];
      const ipdRows = d?.ipd?.data || [];
      const opdSummary = d?.opd?.summary || {};
      const ipdSummary = d?.ipd?.summary || {};
      const totalSummary = d?.total || {};

      if (opdRows.length === 0 && ipdRows.length === 0) {
        toast('No Data Found For These Filters', { variant: 'error' });
        return;
      }

      const n = (v) => Number(v) || 0;
      const rupees = (v) => n(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const patName = (row) =>
        [row.patient?.name_suffix, row.patient?.first_name, row.patient?.last_name].filter(Boolean).join(' ').trim() ||
        '-';
      const serviceTypeLabel = d?.service_type
        ? SERVICE_TYPE.find((t) => t._id === d.service_type)?.name || d.service_type
        : '';
      const titleLabel = d?.service?.service_name || (serviceTypeLabel ? `All ${serviceTypeLabel} Services` : 'All Services');

      const COLS = ['Sr.No.', 'Date', 'Service', 'Patient Name', 'UHID', 'Bill/Adm No.', 'Qty', 'Amount'];

      const rows = [];
      rows.push([`SERVICE COLLECTION REPORT - ${titleLabel}`]);
      rows.push([`Period: ${formatDate(watch('from_date'))} to ${formatDate(watch('to_date'))}`]);
      rows.push([]);

      const pushSection = (title, data, billNoKey, summary) => {
        rows.push([title, '', '', '', '', '', '', `(${data.length} records)`]);
        rows.push(COLS);
        if (data.length === 0) {
          rows.push(['', '', '', 'No records found for this period.']);
        } else {
          data.forEach((row, i) =>
            rows.push([
              i + 1,
              formatDate(row.date),
              row.serviceMaster?.service_name || '-',
              patName(row),
              row.patient?.uhid || '-',
              row[billNoKey] || '-',
              row.service_items?.quantity || 0,
              rupees(row.service_items?.net_amt ?? row.service_items?.amount)
            ])
          );
        }
        rows.push(['', '', '', '', '', 'Patients', summary.patientCount, '']);
        rows.push(['', '', '', '', '', 'Bookings', summary.bookingCount, rupees(summary.amount)]);
        rows.push([]);
      };

      pushSection('OPD SERVICE BOOKINGS', opdRows, 'op_no', opdSummary);
      pushSection('IPD SERVICE BOOKINGS', ipdRows, 'ipd_no', ipdSummary);

      rows.push(['GRAND TOTAL', '', '', '', '', 'Patients', totalSummary.patientCount, '']);
      rows.push(['', '', '', '', '', 'Bookings', totalSummary.bookingCount, rupees(totalSummary.amount)]);

      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [
        { wch: 6 },
        { wch: 11 },
        { wch: 20 },
        { wch: 28 },
        { wch: 10 },
        { wch: 14 },
        { wch: 8 },
        { wch: 14 }
      ];

      const wb = { Sheets: { 'Service Collection Report': ws }, SheetNames: ['Service Collection Report'] };
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: fileType });
      saveAs(blob, `Service_Collection_Report_${watch('from_date')}_to_${watch('to_date')}${fileExtension}`);
    } catch (err) {
      console.error('Excel export error:', err);
      toast('Failed to generate Excel report', { variant: 'error' });
    }
  };

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Service Collection Report
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <Button
                  variant="contained"
                  id={`${lastPathSegment}Print`}
                  disabled={noFilterSelected || !watch('to_date')}
                  onClick={() => window.open(`/print${pathname}?refId=${buildRefId()}`, '_blank')}
                >
                  View Pdf Report
                </Button>
                <Button
                  variant="contained"
                  id={`${lastPathSegment}Print`}
                  disabled={noFilterSelected || !watch('to_date')}
                  onClick={handleExcelReport}
                >
                  Download Excel Report
                </Button>
                <Button variant="contained">Exit</Button>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '60%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={11} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Service Type</Typography>
                    <SelectTag formControlSX={{ flex: 0.8 }} name="service_type" menus={SERVICE_TYPE_MENUS} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Service</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="service"
                      menus={[
                        { _id: 'all', name: 'All' },
                        ...filteredServiceDropdown.map((data) => ({ ...data, name: data.service_name }))
                      ]}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>From Date</Typography>
                    <TextField sx={{ flex: 0.3 }} name="from_date" type="date" />
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>To Date</Typography>
                    <TextField sx={{ flex: 0.3 }} name="to_date" type="date" />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};

export default ServiceCollectionReport;
